import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Add } from "@mui/icons-material";
import TextBox from "../../components/TextBox";
import DatePickV2 from "../../components/DatePickV2";
import TextArea from "../../components/TextArea";
import ButtonSubmit from "../../components/ButtonSubmit";
import TextCurrency from "../../components/TextCurrency";
import { SettlementsAPI } from "../../services/api";
import CustomSnackBar from "../../components/CustomSnackBar";
import {
  creditAllocationAmount,
  creditRemainingAmount,
} from "../../utils/settlementCredits";

function CreditNoteForm({
  setOpen,
  onSave,
  initialData,
  invoices = [],
  hideReason = false,
  hideInvoiceSelect = false,
  lockedInvoiceId = null,
  lockedInvoiceLabel = "",
  linkedSuggestionLabel = "",
  linkedSuggestionKey = "",
  linkedType = "",
  linkedSource = "",
  linkedEffectiveDate = null,
  linkedAdjustmentAmount = null,
  linkedDetailId = null,
  presetReason = "",
  presetAmount = null,
  forAdjustmentsOnly = false,
}) {
  const [form, setForm] = useState({
    number: "",
    amount: 0,
    date: new Date(),
    reason: "",
    invoiceId: "",
  });

  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });
  const [submitting, setSubmitting] = useState(false);
  const reconciliationPreview = {
    amount: form.amount,
    linkedAdjustmentAmount:
      linkedAdjustmentAmount
      ?? initialData?.linkedAdjustmentAmount
      ?? presetAmount
      ?? 0,
    pendingAdjustmentAmount: initialData?.pendingAdjustmentAmount,
    forAdjustmentsOnly: Boolean(
      linkedSuggestionKey
      || initialData?.linkedSuggestionKey
      || forAdjustmentsOnly
      || initialData?.forAdjustmentsOnly
    ),
    linkedSuggestionKey: linkedSuggestionKey || initialData?.linkedSuggestionKey,
  };
  const allocationAmount = creditAllocationAmount(reconciliationPreview);
  const remainingCreditAmount = creditRemainingAmount(reconciliationPreview);

  useEffect(() => {
    if (initialData) {
      setForm({
        number: initialData.number || "",
        amount: Number(initialData.amount || presetAmount || 0),
        date: initialData.date || new Date(),
        reason: initialData.reason || presetReason || "",
        invoiceId: initialData.invoiceId || lockedInvoiceId || "",
      });
      return;
    }

    setForm({
      number: "",
      amount: Number(presetAmount || 0),
      date: new Date(),
      reason: presetReason || "",
      invoiceId: lockedInvoiceId || "",
    });
  }, [initialData, lockedInvoiceId, presetAmount, presetReason]);

  const handleChange = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const validateNumber = async () => {
    const num = String(form.number || "").trim();
    if (!num) return true;
    try {
      await SettlementsAPI.checkCreditNote(num);
      return true;
    } catch (error) {
      const isDup = error?.response?.status === 409;
      setSnack({
        open: true,
        message: isDup
          ? "Error por nota de credito duplicada: ese numero ya existe."
          : "No se pudo validar el numero de la nota de credito. Intenta de nuevo.",
        severity: "error",
      });
      return false;
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (submitting) return;
    setSubmitting(true);

    try {
      const amountNum = Number(form.amount) || 0;
      if (!form.invoiceId) {
        setSnack({
          open: true,
          message: "Selecciona la factura a la que aplica la nota de credito.",
          severity: "error",
        });
        return;
      }
      if (amountNum <= 0) {
        setSnack({ open: true, message: "Monto invalido (debe ser mayor a 0).", severity: "error" });
        return;
      }
      const num = String(form.number || "").trim();
      if (!num) {
        setSnack({ open: true, message: "Numero de nota de credito requerido.", severity: "error" });
        return;
      }

      const ok = await validateNumber();
      if (!ok) return;

      onSave({
        number: num,
        amount: amountNum,
        allocationAmount,
        remainingCreditAmount,
        date: form.date,
        reason: String(form.reason || presetReason || "").trim(),
        invoiceId: Number(form.invoiceId),
        linkedSuggestionKey: linkedSuggestionKey || initialData?.linkedSuggestionKey || "",
        linkedSuggestionLabel: linkedSuggestionLabel || initialData?.linkedSuggestionLabel || "",
        linkedInvoiceLabel: lockedInvoiceLabel || initialData?.linkedInvoiceLabel || "",
        linkedType: linkedType || initialData?.linkedType || "",
        linkedSource: linkedSource || initialData?.linkedSource || "",
        linkedEffectiveDate: linkedEffectiveDate || initialData?.linkedEffectiveDate || null,
        linkedAdjustmentAmount: Number(
          linkedAdjustmentAmount
          ?? initialData?.linkedAdjustmentAmount
          ?? presetAmount
          ?? amountNum
        ),
        linkedDetailId: linkedDetailId || initialData?.linkedDetailId || null,
        forAdjustmentsOnly: Boolean(
          linkedSuggestionKey
          || initialData?.linkedSuggestionKey
          || forAdjustmentsOnly
          || initialData?.forAdjustmentsOnly
        ),
      });
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: 500,
        m: "0 auto",
      }}
    >
      <TextBox
        label="No. nota de credito"
        name="number"
        onChange={handleChange}
        width={"300px"}
        newValue={form.number}
      />
      <TextCurrency
        label="Monto de la nota"
        name="amount"
        onChange={handleChange}
        newValue={form.amount}
        matchTextBox
        width={"300px"}
      />
      <DatePickV2
        name="date"
        label="Fecha"
        value={form.date}
        onChange={handleChange}
        width={"100%"}
        size="small"
        returnType="iso"
      />

      {linkedSuggestionLabel ? (
        <Box
          sx={{
            width: "100%",
            mt: 1,
            p: 1.25,
            borderRadius: 1.5,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>NC vinculada</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {linkedSuggestionLabel}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.secondary" }}>
            {lockedInvoiceLabel || `Factura #${form.invoiceId || "-"}`}
          </Typography>
          <Box sx={{ mt: 1, display: "grid", gap: 0.35 }}>
            <Typography sx={{ fontSize: 12 }}>
              Monto de la nota: <b>Q {Number(form.amount || 0).toFixed(2)}</b>
            </Typography>
            <Typography sx={{ fontSize: 12 }}>
              Monto que conciliará: <b>Q {allocationAmount.toFixed(2)}</b>
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: remainingCreditAmount > 0 ? "warning.main" : "text.secondary",
              }}
            >
              Saldo restante de la nota: <b>Q {remainingCreditAmount.toFixed(2)}</b>
            </Typography>
          </Box>
        </Box>
      ) : null}

      {!hideReason ? (
        <TextArea
          label="Razon"
          name="reason"
          onChange={handleChange}
          newValue={form.reason}
          sx={{ width: "100%" }}
          minRows={2}
        />
      ) : null}

      {!hideInvoiceSelect ? (
        <FormControl size="small" sx={{ width: "100%", mt: 1 }}>
          <InputLabel id="invoice-select-label">Factura</InputLabel>
          <Select
            labelId="invoice-select-label"
            label="Factura"
            value={form.invoiceId}
            onChange={(e) => handleChange("invoiceId", e.target.value)}
            required
          >
            {invoices.map((inv) => (
              <MenuItem key={inv.id} value={inv.id}>
                #{inv.number} · {String(inv.date)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Box sx={{ width: "100%", mt: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Factura</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {lockedInvoiceLabel || `#${form.invoiceId || "-"}`}
          </Typography>
        </Box>
      )}

      <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 1 }}>
        <ButtonSubmit name={submitting ? "Validando..." : "Agregar"} icon={Add} loading={submitting} />
      </Box>

      <CustomSnackBar
        open={snack.open}
        setOpen={(value) => setSnack((prev) => ({ ...prev, open: value }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Box>
  );
}

export default CreditNoteForm;
